import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Chip, Stack } from '@mui/material';

import { DataGrid, FormCard } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { LocalStoragePrefix } from '@/constants';
import { useBuildingInspectionContext } from '@/pages/Tools/BuildingInspection/BuildingInspectionContext';
import { violationsDataGridReducedCols } from '@/pages/Tools/BuildingInspection/dummy_data/dummy_violations';
import { visitNotesDataGridCols } from '@/pages/Tools/BuildingInspection/dummy_data/dummy_visit_notes';
import { inspectionsStatusColor } from '@/pages/Tools/BuildingInspection/Models';
import { useUserStore } from '@/store';
import { getLocalStorageItem } from '@/utils';

export const InspectionVisits = () => {
  const { inspectionId } = useParams();
  const { violations, visitNotes, inspectionVisits } =
    useBuildingInspectionContext();
  const { user } = useUserStore();

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `visit-notes-list-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return visitNotesDataGridCols;
      }

      return mergeColumnSettings(visitNotesDataGridCols, savedSettings);
    },
  );

  const [violationColumnSettings, setViolationColumnSettings] = useState<
    DataGridColSettings[]
  >(() => {
    const savedSettings = getLocalStorageItem({
      key: String(user?._id),
      prefix: `Violations-list-${LocalStoragePrefix.TableSetting}`,
      shouldParse: true,
    });

    if (!savedSettings) {
      return violationsDataGridReducedCols;
    }

    return mergeColumnSettings(violationsDataGridReducedCols, savedSettings);
  });

  const violationsColumns = useDataGridColumns({
    type: 'inspectionViolationsCols',

    columnSettings: violationColumnSettings,
  }).map((column) => ({
    ...column,
    renderCell: (params: any) => {
      return column.renderCell ? column.renderCell(params) : params.value;
    },
  }));
  const columns = useDataGridColumns({
    type: 'inspectionVisitNotes',
    onDelete: (e) => e,
    onEdit: (e) => e,
    columnSettings,
  }).map((column) => ({
    ...column,
    renderCell: (params: any) => {
      return column.renderCell ? column.renderCell(params) : params.value;
    },
  }));

  return (
    <Stack sx={{ overflow: 'scroll', height: '500px', margin: '20px 0' }}>
      {inspectionVisits
        ?.filter((visit) => visit.inspection_id === inspectionId)
        ?.map((visit) => {
          const currentVisitNotes = visitNotes.filter((item) =>
            visit.note_ids.includes(item._id),
          );
          const currentViolations = violations.filter((item) =>
            visit.violation_ids.includes(item._id),
          );
          return (
            <Stack key={visit._id}>
              <header
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 99,
                  backgroundColor: '#F5F7F7',
                  padding: '10px',
                }}
              >
                <h2>
                  Site visist #{visit._id}{' '}
                  <Chip
                    size="small"
                    label={visit.status}
                    style={{
                      margin: '0 5px',
                      color: '#fff',
                      backgroundColor: inspectionsStatusColor[visit.status],
                    }}
                  />{' '}
                  on {visit.scheduledDateTime}
                </h2>
              </header>
              <FormCard title="Violations" sx={{ flexDirection: 'column' }}>
                <DataGrid
                  hideFilter
                  hideFooterPagination
                  hideFooter
                  key="inspectionViolationsCols"
                  name="violations-list"
                  columns={violationsColumns}
                  disableMultipleColumnsSorting={true}
                  fetchData={() => Promise.resolve({ rows: currentViolations })}
                  disableRowSelectionOnClick
                  autoPageSize={false}
                  columnSettings={violationColumnSettings}
                  setColumnSettings={setViolationColumnSettings}
                  // rows={currentViolations}
                  sx={{ maxHeight: '300px' }}
                  slots={{ toolbar: null }}
                />
              </FormCard>
              <FormCard
                title="Site visit notes"
                sx={{ flexDirection: 'column' }}
              >
                <DataGrid
                  hideFilter
                  hideFooterPagination
                  hideFooter
                  key="inspectionVisitNotes"
                  name="visit-notes-list"
                  columnSettings={columnSettings}
                  setColumnSettings={setColumnSettings}
                  columns={columns}
                  disableMultipleColumnsSorting={true}
                  fetchData={() => Promise.resolve({ rows: currentVisitNotes })}
                  disableRowSelectionOnClick
                  autoPageSize={false}
                  sx={{ maxHeight: '300px' }}
                  slots={{ toolbar: null }}
                />
              </FormCard>
            </Stack>
          );
        })}
    </Stack>
  );
};
